import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { API_BASE_URL } from '../components/config/api';
import {
  Download, ChevronRight, ChevronDown, Check, Loader2,
  AlertCircle, ArrowLeft, ShoppingBag, Plus, Trash2
} from 'lucide-react';

// ── Types ────────────────────────────────────────────────────────────────────

interface SimpleIngredientLink {
  store_ingredient_id: number;
  name: string;
  category?: string;
  is_default: boolean;
  is_removable: boolean;
  extra_price_override: number | null;
}

// How an efood option group is imported into the native catalog.
//  - 'size'   → values become product_sizes (single-choice, price-defining)
//  - 'option' → values become a store_option_group + choices (extra)
//  - 'ignore' → not imported
type GroupRole = 'size' | 'option' | 'ignore';

interface ImportOptionValue {
  efood_option_id: string;   // platform option id — kept for order auto-mapping
  efood_option_code?: string;
  name: string;
  price: number;             // size role: absolute price; option role: add-on price
  is_default: boolean;
}

interface ImportOptionGroup {
  efood_group_id: string;
  name: string;
  selection_type: 'single' | 'multiple'; // radio→single, checkbox→multiple
  role: GroupRole;
  values: ImportOptionValue[];
}

interface ImportRow {
  id: string;                // efood product id (platform_product_id)
  code?: string;
  name: string;
  description: string;
  price: number;
  vat: number;
  category_name: string;
  include: boolean;
  product_type: 'ΠΡΟΙΟΝ' | 'ΕΜΠΟΡΕΥΜΑ';
  option_groups: ImportOptionGroup[];
  simple_ingredients: SimpleIngredientLink[];
  plastic_fee_count: number;  // single-use plastic fees (code 16) auto-added per unit
  exists?: boolean;           // re-import: product already in the catalog
}

interface StoreIngredient {
  id: number;
  name: string;
  extra_price: number;
  category?: string;
}

type Step = 'intro' | 'editing' | 'done';

interface Props {
  onImportComplete: () => void;
  reimport?: boolean;       // opened on-demand to pull NEW products (vs empty-catalog setup)
  onClose?: () => void;     // present when rendered as a modal
}

// ── Constants ────────────────────────────────────────────────────────────────

const VAT_OPTIONS = [6, 13, 24];
const DEFAULT_VAT = 13;
const toUC = (s: string) => s.toUpperCase();
// Accent-insensitive name key (lowercase + strip Greek/Latin accents + collapse spaces)
const normName = (s: string) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim();

// ── Component ────────────────────────────────────────────────────────────────

const CatalogImportWizard: React.FC<Props> = ({ onImportComplete, reimport = false, onClose }) => {
  const { store } = useAuth();
  const { t } = useTranslation(['products', 'common']);
  const token = localStorage.getItem('xentrix_token');
  const isNonWarehouse = store?.warehouse === false;

  const [step, setStep] = useState<Step>('intro');
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<{ createdCategories: number; createdProducts: number; skippedProducts: number } | null>(null);
  const [bulkVat, setBulkVat] = useState<number>(DEFAULT_VAT);
  const [bulkFee, setBulkFee] = useState<number>(2);
  const [bulkType, setBulkType] = useState<'ΠΡΟΙΟΝ' | 'ΕΜΠΟΡΕΥΜΑ'>('ΠΡΟΙΟΝ');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [autoUppercase, setAutoUppercase] = useState(true);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [storeIngredients, setStoreIngredients] = useState<StoreIngredient[]>([]);

  // Fetch store ingredients once (needed for non-warehouse stores)
  useEffect(() => {
    if (!isNonWarehouse || !store?.id) return;
    fetch(`${API_BASE_URL}/api/stores/${store.id}/store-ingredients`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => setStoreIngredients(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [store?.id, isNonWarehouse]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchEfoodCatalog = async () => {
    setFetching(true);
    setFetchError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/product-mappings/${store!.id}/catalog/efood`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || err.details || t('products:efoodConnectionFailed'));
      }
      const data = await res.json();

      // Re-import: pull the current catalog so already-imported products can be
      // pre-unchecked (cashier only reviews the genuinely new ones).
      let existingKeys = new Set<string>();
      if (reimport) {
        try {
          const exRes = await fetch(`${API_BASE_URL}/api/stores/${store!.id}/products`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (exRes.ok) {
            const exData = await exRes.json();
            const list = Array.isArray(exData) ? exData : (exData.products || []);
            existingKeys = new Set(list.map((pr: any) => normName(String(pr.name || ''))));
          }
        } catch { /* non-fatal: fall back to showing all */ }
      }

      const importRows: ImportRow[] = (data.products || []).map((p: any) => {
        // Keep ALL efood option groups; pre-classify role from modifier_type.
        // The cashier reviews each group (Size / Option / Ignore) before import.
        const option_groups: ImportOptionGroup[] = (p.option_groups || []).map((og: any) => ({
          efood_group_id: String(og.id ?? og.name ?? ''),
          name: og.name || '',
          selection_type: og.type === 'checkbox' ? 'multiple' : 'single',
          role: (og.modifier_type === 'size' ? 'size' : 'option') as GroupRole,
          values: (og.values || []).map((v: any, i: number) => ({
            efood_option_id: String(v.id ?? v.code ?? ''),
            efood_option_code: v.code != null ? String(v.code) : undefined,
            name: autoUppercase ? toUC(v.name || '') : (v.name || ''),
            price: parseFloat(v.price) || 0,
            // single-choice groups get a default; multi-choice extras start unselected
            is_default: og.type !== 'checkbox' && i === 0,
          })),
        }));

        const exists = reimport && existingKeys.has(normName(String(p.name || '')));

        return {
          id: p.id,
          code: p.code,
          name: autoUppercase ? toUC(p.name || '') : (p.name || ''),
          description: p.description || '',
          price: p.price || 0,
          vat: DEFAULT_VAT,
          category_name: autoUppercase ? toUC(p.category_name || t('products:other')) : (p.category_name || t('products:other')),
          include: !exists,           // new products checked by default; existing ones off
          product_type: 'ΠΡΟΙΟΝ' as const,
          option_groups,
          simple_ingredients: [],
          plastic_fee_count: 0,
          exists,
        };
      });

      // Re-import with brand-new products only: surface them first.
      importRows.sort((a, b) => Number(a.exists) - Number(b.exists));

      setRows(importRows);
      setStep('editing');
    } catch (e: any) {
      setFetchError(e.message);
    } finally {
      setFetching(false);
    }
  };

  // ── Row helpers ────────────────────────────────────────────────────────────

  const updateRow = (id: string, field: keyof ImportRow, value: any) =>
    setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));

  const toggleExpand = (id: string) =>
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // Option groups
  const updateGroup = (rowId: string, gIdx: number, field: keyof ImportOptionGroup, value: any) =>
    setRows(prev => prev.map(r => r.id !== rowId ? r : {
      ...r,
      option_groups: r.option_groups.map((g, i) => i === gIdx ? { ...g, [field]: value } : g)
    }));

  const updateGroupValue = (rowId: string, gIdx: number, vIdx: number, field: keyof ImportOptionValue, value: any) =>
    setRows(prev => prev.map(r => r.id !== rowId ? r : {
      ...r,
      option_groups: r.option_groups.map((g, i) => {
        if (i !== gIdx) return g;
        const single = g.selection_type === 'single';
        return {
          ...g,
          values: g.values.map((v, j) => {
            if (j === vIdx) return { ...v, [field]: value };
            // For single-choice groups, only one default
            if (field === 'is_default' && value === true && single) return { ...v, is_default: false };
            return v;
          })
        };
      })
    }));

  const addGroup = (rowId: string) =>
    setRows(prev => prev.map(r => r.id !== rowId ? r : {
      ...r,
      option_groups: [...r.option_groups, {
        efood_group_id: `manual-${Date.now()}`,
        name: '',
        selection_type: 'single',
        role: 'size',
        values: [{ efood_option_id: '', name: '', price: 0, is_default: true }],
      }]
    }));

  const removeGroup = (rowId: string, gIdx: number) =>
    setRows(prev => prev.map(r => r.id !== rowId ? r : {
      ...r,
      option_groups: r.option_groups.filter((_, i) => i !== gIdx)
    }));

  const addGroupValue = (rowId: string, gIdx: number) =>
    setRows(prev => prev.map(r => r.id !== rowId ? r : {
      ...r,
      option_groups: r.option_groups.map((g, i) => i !== gIdx ? g : {
        ...g,
        values: [...g.values, { efood_option_id: '', name: '', price: 0, is_default: g.values.length === 0 }]
      })
    }));

  const removeGroupValue = (rowId: string, gIdx: number, vIdx: number) =>
    setRows(prev => prev.map(r => r.id !== rowId ? r : {
      ...r,
      option_groups: r.option_groups.map((g, i) => i !== gIdx ? g : {
        ...g,
        values: g.values.filter((_, j) => j !== vIdx)
      })
    }));

  // Ingredients
  const addIngredient = (rowId: string, si: StoreIngredient) =>
    setRows(prev => prev.map(r => {
      if (r.id !== rowId) return r;
      if (r.simple_ingredients.some(i => i.store_ingredient_id === si.id)) return r;
      return {
        ...r,
        simple_ingredients: [...r.simple_ingredients, {
          store_ingredient_id: si.id,
          name: si.name,
          category: si.category,
          is_default: true,
          is_removable: true,
          extra_price_override: null,
        }]
      };
    }));

  const updateIngredient = (rowId: string, siId: number, field: keyof SimpleIngredientLink, value: any) =>
    setRows(prev => prev.map(r => r.id !== rowId ? r : {
      ...r,
      simple_ingredients: r.simple_ingredients.map(i =>
        i.store_ingredient_id === siId ? { ...i, [field]: value } : i
      )
    }));

  const removeIngredient = (rowId: string, siId: number) =>
    setRows(prev => prev.map(r => r.id !== rowId ? r : {
      ...r,
      simple_ingredients: r.simple_ingredients.filter(i => i.store_ingredient_id !== siId)
    }));

  // Bulk
  const applyBulkVat = () =>
    setRows(prev => prev.map(r => r.include ? { ...r, vat: bulkVat } : r));

  // Set plastic-fee count on the currently filtered + included rows (e.g. filter
  // to "Καφέδες" then apply 2).
  const applyBulkFee = () => {
    const visibleIds = new Set(filteredRows.map(r => r.id));
    setRows(prev => prev.map(r => (visibleIds.has(r.id) && r.include) ? { ...r, plastic_fee_count: bulkFee } : r));
  };

  // Set product type (ΠΡΟΙΟΝ / ΕΜΠΟΡΕΥΜΑ) on the currently filtered + included rows.
  const applyBulkType = () => {
    const visibleIds = new Set(filteredRows.map(r => r.id));
    setRows(prev => prev.map(r => (visibleIds.has(r.id) && r.include) ? { ...r, product_type: bulkType } : r));
  };

  const toggleAll = (include: boolean) => {
    const visibleIds = filteredRows.map(r => r.id);
    setRows(prev => prev.map(r => visibleIds.includes(r.id) ? { ...r, include } : r));
  };

  const toggleUppercase = (enabled: boolean) => {
    setAutoUppercase(enabled);
    setRows(prev => prev.map(r => ({
      ...r,
      name: enabled ? toUC(r.name) : r.name,
      category_name: enabled ? toUC(r.category_name) : r.category_name,
      option_groups: r.option_groups.map(g => ({
        ...g,
        values: g.values.map(v => ({ ...v, name: enabled ? toUC(v.name) : v.name })),
      })),
    })));
  };

  // ── Import ─────────────────────────────────────────────────────────────────

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/product-mappings/${store!.id}/catalog/create-from-efood`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ products: rows })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || t('products:importFailed'));
      }
      setResult(await res.json());
      setStep('done');
    } catch (e: any) {
      alert(e.message);
    } finally {
      setImporting(false);
    }
  };

  // ── Derived ────────────────────────────────────────────────────────────────

  // Array.from αντί για spread: το tsconfig είναι target es5 και το spread ενός
  // Set κόβει τον έλεγχο τύπων με TS2802 (χρειαζόταν downlevelIteration).
  const allCategories = Array.from(new Set(rows.map(r => r.category_name))).sort();
  const filteredRows = filterCategory === 'all' ? rows : rows.filter(r => r.category_name === filterCategory);
  const includedCount = rows.filter(r => r.include).length;

  // ── INTRO ──────────────────────────────────────────────────────────────────
  if (step === 'intro') {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-4">
        <div className="w-20 h-20 bg-orange-500/10 rounded-full flex items-center justify-center mb-6">
          <ShoppingBag className="w-10 h-10 text-orange-600 dark:text-orange-400" />
        </div>
        <h2 className="text-2xl font-bold text-t1 mb-2">{reimport ? t('products:importNewProducts') : t('products:catalogEmpty')}</h2>
        <p className="text-t3 text-center max-w-md mb-8">
          {reimport ? t('products:importNewProductsDesc') : t('products:catalogEmptyDesc')}
        </p>
        {fetchError && (
          <div className="flex items-center gap-2 mb-6 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-700 dark:text-red-300 text-sm max-w-md">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {fetchError}
          </div>
        )}
        <div className="flex flex-col items-center gap-3">
          <button
            onClick={fetchEfoodCatalog}
            disabled={fetching}
            className="flex items-center gap-3 px-8 py-4 bg-orange-500 hover:bg-orange-400 disabled:bg-surface-3 text-white rounded-2xl font-semibold text-lg transition-all active:scale-95"
          >
            {fetching ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
            {fetching ? t('products:loadingFromEfood') : t('products:importFromEfood')}
          </button>
          <p className="text-xs text-t4">{t('products:efoodCredentialsRequired')}</p>
          {onClose && (
            <button onClick={onClose} className="mt-2 px-4 py-2 text-sm text-t3 hover:text-t1 transition-colors">
              {t('common:cancel')}
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── DONE ──────────────────────────────────────────────────────────────────
  if (step === 'done' && result) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-4">
        <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mb-6">
          <Check className="w-10 h-10 text-green-600 dark:text-green-400" />
        </div>
        <h2 className="text-2xl font-bold text-t1 mb-2">{t('products:importComplete')}</h2>
        <div className="flex gap-6 mt-4 mb-8">
          <div className="text-center">
            <p className="text-3xl font-bold text-t1">{result.createdCategories}</p>
            <p className="text-sm text-t3 mt-1">{t('products:categories')}</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600 dark:text-green-400">{result.createdProducts}</p>
            <p className="text-sm text-t3 mt-1">{t('products:title')}</p>
          </div>
          {result.skippedProducts > 0 && (
            <div className="text-center">
              <p className="text-3xl font-bold text-t3">{result.skippedProducts}</p>
              <p className="text-sm text-t3 mt-1">{t('products:skipped')}</p>
            </div>
          )}
        </div>
        <button
          onClick={onImportComplete}
          className="px-8 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-semibold transition-all active:scale-95"
        >
          {t('products:viewProducts')}
        </button>
      </div>
    );
  }

  // ── EDITING ────────────────────────────────────────────────────────────────
  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setStep('intro')}
            className="p-2 rounded-lg hover:bg-surface-2 text-t3 hover:text-t1 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h2 className="text-lg font-bold text-t1">{t('products:editEfoodCatalog')}</h2>
            <p className="text-sm text-t3">{t('products:productsCount', { count: rows.length, selected: includedCount })}</p>
          </div>
        </div>
        <button
          onClick={handleImport}
          disabled={importing || includedCount === 0}
          className="flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-500 disabled:bg-surface-3 disabled:text-t3 text-white rounded-xl font-semibold text-sm transition-all active:scale-95"
        >
          {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
          {importing ? t('products:importing') : t('products:importNProducts', { count: includedCount })}
        </button>
      </div>

      {/* Bulk controls */}
      <div className="flex flex-wrap items-center gap-3 p-4 bg-surface-2 dark:bg-surface-1 border border-line rounded-xl">
        <span className="text-sm text-t3 font-medium">{t('products:bulkActions')}</span>
        <button onClick={() => toggleAll(true)} className="px-3 py-1.5 text-xs bg-surface-2 hover:bg-surface-3 text-t2 rounded-lg transition-colors">
          {t('products:selectAll')}
        </button>
        <button onClick={() => toggleAll(false)} className="px-3 py-1.5 text-xs bg-surface-2 hover:bg-surface-3 text-t2 rounded-lg transition-colors">
          {t('products:deselectAll')}
        </button>
        <button
          onClick={() => toggleUppercase(!autoUppercase)}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg font-medium transition-colors border ${
            autoUppercase ? 'bg-indigo-600/20 border-indigo-500/50 text-indigo-700 dark:text-indigo-300' : 'bg-surface-2 border-line-strong text-t3 hover:text-t2'
          }`}
        >
          <span className="font-bold tracking-wider">ΑΑ</span>
          {t('products:uppercase')}
        </button>

        <div className="flex items-center gap-2 ml-auto">
          <span className="text-xs text-t3">{t('products:vatOnSelected')}</span>
          <select value={bulkVat} onChange={e => setBulkVat(Number(e.target.value))}
            className="px-2 py-1.5 text-xs bg-surface-2 border border-line-strong text-t1 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500">
            {VAT_OPTIONS.map(v => <option key={v} value={v}>{v}%</option>)}
          </select>
          <button onClick={applyBulkVat} className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors">
            {t('products:apply')}
          </button>
        </div>

        {/* Plastic fee on the currently filtered rows (filter to a drinks category, then apply) */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-t3">{t('products:plasticFeeOnFiltered')}</span>
          <input type="number" min="0" max="9" value={bulkFee}
            onChange={e => setBulkFee(Math.max(0, parseInt(e.target.value) || 0))}
            className="w-14 px-2 py-1.5 text-xs bg-surface-2 border border-line-strong text-t1 rounded-lg focus:outline-none focus:ring-1 focus:ring-cyan-500" />
          <button onClick={applyBulkFee} className="px-3 py-1.5 text-xs bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg transition-colors">
            {t('products:apply')}
          </button>
        </div>

        {/* Product type on the currently filtered rows */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-t3">{t('products:typeOnFiltered')}</span>
          <select value={bulkType} onChange={e => setBulkType(e.target.value as 'ΠΡΟΙΟΝ' | 'ΕΜΠΟΡΕΥΜΑ')}
            className="px-2 py-1.5 text-xs bg-surface-2 border border-line-strong text-t1 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500">
            <option value="ΠΡΟΙΟΝ">{t('products:productTypeLabel')}</option>
            <option value="ΕΜΠΟΡΕΥΜΑ">{t('products:goodsTypeLabel')}</option>
          </select>
          <button onClick={applyBulkType} className="px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors">
            {t('products:apply')}
          </button>
        </div>

        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
          className="px-2 py-1.5 text-xs bg-surface-2 border border-line-strong text-t1 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500">
          <option value="all">{t('products:allCategories')}</option>
          {allCategories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-surface-2 dark:bg-surface-1 border border-line rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line bg-surface-0/50">
                <th className="px-3 py-3 w-10"></th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-t3 uppercase tracking-wider">{t('products:category')}</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-t3 uppercase tracking-wider">{t('common:name')}</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-t3 uppercase tracking-wider">{t('products:description')}</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-t3 uppercase tracking-wider w-24">{t('products:priceEuro')}</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-t3 uppercase tracking-wider w-20">{t('products:vatRate')}</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-t3 uppercase tracking-wider w-28">{t('products:type')}</th>
                <th className="px-3 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => {
                const isExpanded = expandedRows.has(row.id);
                const hasGroups = row.option_groups.length > 0;
                const hasIngredients = row.simple_ingredients.length > 0;
                // Price is defined by sizes when a size-role group exists.
                const hasSizeGroup = row.option_groups.some(g => g.role === 'size' && g.values.length > 0);

                return (
                  <React.Fragment key={row.id}>
                    {/* Main row */}
                    <tr className={`border-b border-line/50 transition-colors ${
                      row.include ? 'hover:bg-surface-3 dark:hover:bg-surface-2/20' : 'opacity-40'
                    } ${isExpanded ? 'bg-surface-2 dark:bg-surface-2/20' : ''}`}>

                      {/* Include toggle */}
                      <td className="px-3 py-2">
                        <button
                          onClick={() => updateRow(row.id, 'include', !row.include)}
                          className={`w-6 h-6 rounded-md flex items-center justify-center transition-colors ${
                            row.include ? 'bg-blue-600 text-white' : 'bg-surface-2 border border-line-strong text-t4'
                          }`}
                        >
                          {row.include && <Check className="w-3.5 h-3.5" />}
                        </button>
                      </td>

                      {/* Category */}
                      <td className="px-3 py-2">
                        <input value={row.category_name}
                          onChange={e => updateRow(row.id, 'category_name', autoUppercase ? toUC(e.target.value) : e.target.value)}
                          className="w-full px-2 py-1 bg-surface-2 border border-line-strong/50 rounded-lg text-t1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500" />
                      </td>

                      {/* Name */}
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          <input value={row.name}
                            onChange={e => updateRow(row.id, 'name', autoUppercase ? toUC(e.target.value) : e.target.value)}
                            className="w-full px-2 py-1 bg-surface-2 border border-line-strong/50 rounded-lg text-t1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500" />
                          {row.exists && (
                            <span className="shrink-0 px-1.5 py-0.5 text-[10px] rounded bg-surface-3 dark:bg-surface-3/60 text-t2 whitespace-nowrap">{t('products:alreadyExists')}</span>
                          )}
                        </div>
                      </td>

                      {/* Description */}
                      <td className="px-3 py-2">
                        <input value={row.description} placeholder="—"
                          onChange={e => updateRow(row.id, 'description', e.target.value)}
                          className="w-full px-2 py-1 bg-surface-2 border border-line-strong/50 rounded-lg text-t2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-t4" />
                      </td>

                      {/* Price (hidden when sizes define the price) */}
                      <td className="px-3 py-2">
                        {hasSizeGroup ? (
                          <span className="text-xs text-t4 italic">{t('products:priceBySize', { defaultValue: 'ανά μέγεθος' })}</span>
                        ) : (
                          <div className="flex items-center gap-1">
                            <span className="text-t4 text-xs">€</span>
                            <input type="number" min="0" step="0.10" value={row.price}
                              onChange={e => updateRow(row.id, 'price', parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1 bg-surface-2 border border-line-strong/50 rounded-lg text-t1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500" />
                          </div>
                        )}
                      </td>

                      {/* VAT */}
                      <td className="px-3 py-2">
                        <select value={row.vat} onChange={e => updateRow(row.id, 'vat', Number(e.target.value))}
                          className="w-full px-2 py-1 bg-surface-2 border border-line-strong/50 rounded-lg text-t1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500">
                          {VAT_OPTIONS.map(v => <option key={v} value={v}>{v}%</option>)}
                        </select>
                      </td>

                      {/* Product type */}
                      <td className="px-3 py-2">
                        <select value={row.product_type}
                          onChange={e => updateRow(row.id, 'product_type', e.target.value as 'ΠΡΟΙΟΝ' | 'ΕΜΠΟΡΕΥΜΑ')}
                          className="w-full px-2 py-1 bg-surface-2 border border-line-strong/50 rounded-lg text-t1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500">
                          <option value="ΠΡΟΙΟΝ">{t('products:productTypeLabel')}</option>
                          <option value="ΕΜΠΟΡΕΥΜΑ">{t('products:goodsTypeLabel')}</option>
                        </select>
                      </td>

                      {/* Expand button */}
                      <td className="px-3 py-2">
                        <button
                          onClick={() => toggleExpand(row.id)}
                          title={t('products:sizesAndIngredients')}
                          className={`relative w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                            isExpanded
                              ? 'bg-surface-3 text-t1'
                              : hasGroups || hasIngredients
                                ? 'bg-blue-500/20 text-blue-600 dark:text-blue-400 hover:bg-blue-500/30'
                                : 'bg-surface-2 dark:bg-surface-2/50 text-t4 hover:bg-surface-2 hover:text-t2'
                          }`}
                        >
                          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>
                      </td>
                    </tr>

                    {/* Expanded detail panel */}
                    {isExpanded && (
                      <tr className="border-b border-line/50">
                        <td colSpan={8} className="px-4 py-4 bg-surface-0/40">
                          <div className="space-y-4">

                            {/* ── Plastic fee (single-use, code 16) ── */}
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs font-semibold text-t2 uppercase tracking-wider">{t('products:plasticFee')}</span>
                              <input type="number" min="0" max="9" value={row.plastic_fee_count}
                                onChange={e => updateRow(row.id, 'plastic_fee_count', Math.max(0, parseInt(e.target.value) || 0))}
                                className="w-16 px-2 py-1 bg-surface-2 border border-line-strong/50 rounded-lg text-t1 text-xs focus:outline-none focus:ring-1 focus:ring-cyan-500" />
                              <span className="text-[10px] text-t4">{t('products:plasticFeeHint')}</span>
                            </div>

                            {/* ── Option groups (sizes / extras) ── */}
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <div>
                                  <span className="text-xs font-semibold text-t2 uppercase tracking-wider">{t('products:optionGroups')}</span>
                                  <p className="text-[10px] text-t4 mt-0.5">{t('products:optionGroupsHint')}</p>
                                </div>
                                <button onClick={() => addGroup(row.id)}
                                  className="flex items-center gap-1 px-2.5 py-1 text-xs bg-surface-2 hover:bg-surface-3 text-t2 rounded-lg transition-colors shrink-0">
                                  <Plus className="w-3 h-3" /> {t('products:addGroup')}
                                </button>
                              </div>

                              {row.option_groups.length === 0 ? (
                                <p className="text-xs text-t4 py-2">{t('products:noOptionGroups')}</p>
                              ) : (
                                <div className="space-y-2">
                                  {row.option_groups.map((g, gIdx) => {
                                    const roleBox = g.role === 'size' ? 'border-blue-500/40 bg-blue-500/5'
                                      : g.role === 'option' ? 'border-emerald-500/30 bg-emerald-500/5'
                                      : 'border-line bg-surface-1/40 opacity-60';
                                    return (
                                      <div key={gIdx} className={`border rounded-lg p-2.5 ${roleBox}`}>
                                        {/* Group header */}
                                        <div className="flex flex-wrap items-center gap-2 mb-2">
                                          <input value={g.name}
                                            placeholder={t('products:groupNamePlaceholder')}
                                            onChange={e => updateGroup(row.id, gIdx, 'name', autoUppercase ? toUC(e.target.value) : e.target.value)}
                                            className="flex-1 min-w-[120px] px-2 py-1 bg-surface-2 border border-line-strong/50 rounded-lg text-t1 text-xs font-medium focus:outline-none focus:ring-1 focus:ring-blue-500" />
                                          <select value={g.selection_type}
                                            onChange={e => updateGroup(row.id, gIdx, 'selection_type', e.target.value)}
                                            className="px-2 py-1 text-xs bg-surface-2 border border-line-strong/50 rounded-lg text-t2 focus:outline-none">
                                            <option value="single">{t('products:single')}</option>
                                            <option value="multiple">{t('products:multiple')}</option>
                                          </select>
                                          <select value={g.role}
                                            onChange={e => updateGroup(row.id, gIdx, 'role', e.target.value)}
                                            className={`px-2 py-1 text-xs rounded-lg border focus:outline-none font-medium ${
                                              g.role === 'size' ? 'bg-blue-600/20 border-blue-500/50 text-blue-800 dark:text-blue-200'
                                              : g.role === 'option' ? 'bg-emerald-600/20 border-emerald-500/50 text-emerald-800 dark:text-emerald-200'
                                              : 'bg-surface-2 border-line-strong text-t3'}`}>
                                            <option value="size">{t('products:roleSize')}</option>
                                            <option value="option">{t('products:roleOption')}</option>
                                            <option value="ignore">{t('products:roleIgnore')}</option>
                                          </select>
                                          <button onClick={() => removeGroup(row.id, gIdx)}
                                            className="w-6 h-6 flex items-center justify-center rounded text-t4 hover:text-red-600 dark:hover:text-red-400 transition-colors">
                                            <Trash2 className="w-3 h-3" />
                                          </button>
                                        </div>

                                        {/* Values */}
                                        {g.role !== 'ignore' && (
                                          <div className="space-y-1.5 pl-1">
                                            <div className="grid grid-cols-[1fr_110px_46px_24px] gap-2 px-1">
                                              <span className="text-xs text-t4">{t('products:optionName')}</span>
                                              <span className="text-xs text-t4">{g.role === 'size' ? t('products:price') : t('products:extraPrice')}</span>
                                              <span className="text-xs text-t4 text-center">{t('products:default')}</span>
                                              <span></span>
                                            </div>
                                            {g.values.map((v, vIdx) => (
                                              <div key={vIdx} className="grid grid-cols-[1fr_110px_46px_24px] gap-2 items-center">
                                                <input value={v.name}
                                                  placeholder={t('products:optionName')}
                                                  onChange={e => updateGroupValue(row.id, gIdx, vIdx, 'name', autoUppercase ? toUC(e.target.value) : e.target.value)}
                                                  className="px-2 py-1 bg-surface-2 border border-line-strong/50 rounded-lg text-t1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500" />
                                                <div className="flex items-center gap-1">
                                                  <span className="text-t4 text-xs">€</span>
                                                  <input type="number" step="0.10" value={v.price}
                                                    onChange={e => updateGroupValue(row.id, gIdx, vIdx, 'price', parseFloat(e.target.value) || 0)}
                                                    className="w-full px-2 py-1 bg-surface-2 border border-line-strong/50 rounded-lg text-t1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500" />
                                                </div>
                                                <div className="flex justify-center">
                                                  <button
                                                    onClick={() => updateGroupValue(row.id, gIdx, vIdx, 'is_default', g.selection_type === 'single' ? true : !v.is_default)}
                                                    className={`w-5 h-5 border-2 transition-colors flex items-center justify-center ${
                                                      g.selection_type === 'single' ? 'rounded-full' : 'rounded'
                                                    } ${v.is_default ? 'border-blue-500 bg-blue-500' : 'border-line-strong'}`}>
                                                    {g.selection_type === 'multiple' && v.is_default && <Check className="w-3 h-3 text-t1" />}
                                                  </button>
                                                </div>
                                                <button onClick={() => removeGroupValue(row.id, gIdx, vIdx)}
                                                  className="w-6 h-6 flex items-center justify-center rounded text-t4 hover:text-red-600 dark:hover:text-red-400 transition-colors">
                                                  <Trash2 className="w-3 h-3" />
                                                </button>
                                              </div>
                                            ))}
                                            <button onClick={() => addGroupValue(row.id, gIdx)}
                                              className="flex items-center gap-1 px-2 py-1 text-xs text-t3 hover:text-t2 transition-colors">
                                              <Plus className="w-3 h-3" /> {t('products:addOption')}
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>

                            {/* ── Ingredients (non-warehouse + ΠΡΟΙΟΝ only) ── */}
                            {isNonWarehouse && row.product_type === 'ΠΡΟΙΟΝ' && (
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-xs font-semibold text-t2 uppercase tracking-wider">{t('products:ingredients')}</span>
                                  <select
                                    defaultValue=""
                                    onChange={e => {
                                      const si = storeIngredients.find(s => s.id === parseInt(e.target.value));
                                      if (si) { addIngredient(row.id, si); e.currentTarget.value = ''; }
                                    }}
                                    className="px-2 py-1 text-xs bg-surface-2 border border-line-strong text-t1 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 max-w-[180px]"
                                  >
                                    <option value="" disabled>{t('products:addIngredientOption')}</option>
                                    {storeIngredients
                                      .filter(si => !row.simple_ingredients.some(i => i.store_ingredient_id === si.id))
                                      .map(si => (
                                        <option key={si.id} value={si.id}>{si.name}</option>
                                      ))}
                                  </select>
                                </div>

                                {row.simple_ingredients.length === 0 ? (
                                  <p className="text-xs text-t4 py-2">{t('products:noIngredientsAdded')}</p>
                                ) : (
                                  <div className="space-y-1.5">
                                    <div className="grid grid-cols-[1fr_80px_50px_50px_24px] gap-2 px-1">
                                      <span className="text-xs text-t4">{t('products:ingredient')}</span>
                                      <span className="text-xs text-t4">{t('products:overridePrice')}</span>
                                      <span className="text-xs text-t4">Default</span>
                                      <span className="text-xs text-t4">{t('products:removable')}</span>
                                      <span></span>
                                    </div>
                                    {row.simple_ingredients.map(ing => (
                                      <div key={ing.store_ingredient_id} className="grid grid-cols-[1fr_80px_50px_50px_24px] gap-2 items-center">
                                        <span className="text-xs text-t2 truncate">{ing.name}</span>
                                        <input
                                          type="number" step="0.10" placeholder="—"
                                          value={ing.extra_price_override ?? ''}
                                          onChange={e => updateIngredient(row.id, ing.store_ingredient_id, 'extra_price_override', e.target.value ? parseFloat(e.target.value) : null)}
                                          className="px-2 py-1 bg-surface-2 border border-line-strong/50 rounded-lg text-t1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                                        />
                                        <div className="flex justify-center">
                                          <button
                                            onClick={() => updateIngredient(row.id, ing.store_ingredient_id, 'is_default', !ing.is_default)}
                                            className={`w-5 h-5 rounded border-2 transition-colors flex items-center justify-center ${
                                              ing.is_default ? 'border-blue-500 bg-blue-500' : 'border-line-strong'
                                            }`}
                                          >
                                            {ing.is_default && <Check className="w-3 h-3 text-t1" />}
                                          </button>
                                        </div>
                                        <div className="flex justify-center">
                                          <button
                                            onClick={() => updateIngredient(row.id, ing.store_ingredient_id, 'is_removable', !ing.is_removable)}
                                            className={`w-5 h-5 rounded border-2 transition-colors flex items-center justify-center ${
                                              ing.is_removable ? 'border-amber-500 bg-amber-500/20' : 'border-line-strong'
                                            }`}
                                          >
                                            {ing.is_removable && <Check className="w-3 h-3 text-amber-700 dark:text-amber-400" />}
                                          </button>
                                        </div>
                                        <button onClick={() => removeIngredient(row.id, ing.store_ingredient_id)}
                                          className="w-6 h-6 flex items-center justify-center rounded text-t4 hover:text-red-600 dark:hover:text-red-400 transition-colors">
                                          <Trash2 className="w-3 h-3" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {filteredRows.length === 0 && (
            <div className="text-center py-12 text-t4">{t('products:noProducts')}</div>
          )}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="sticky bottom-0 bg-surface-0 border-t border-line -mx-4 px-4 py-3 flex items-center justify-between">
        <p className="text-sm text-t3">
          {t('products:selectedOfTotalText', { selected: includedCount, total: rows.length })}
        </p>
        <button
          onClick={handleImport}
          disabled={importing || includedCount === 0}
          className="flex items-center gap-2 px-6 py-2.5 bg-green-600 hover:bg-green-500 disabled:bg-surface-3 disabled:text-t3 text-white rounded-xl font-semibold text-sm transition-all active:scale-95"
        >
          {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          {importing ? t('products:importing') : t('products:importNProducts', { count: includedCount })}
        </button>
      </div>
    </div>
  );
};

export default CatalogImportWizard;
